import React, { useEffect, useRef, useState } from 'react'
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react'

interface NotesPopoverProps {
  isOpen: boolean
  onClose: () => void
  referenceElement: HTMLElement | null
  initialNotes: string
  onSave: (notes: string) => void
  entityType: 'word' | 'rhyme'
  entityLabel: string
}

const MAX_CHARS = 500

/**
 * NotesPopover - Floating popover for adding/editing notes
 *
 * Uses Floating UI to avoid sidebar clipping. Auto-focuses textarea,
 * shows character count, handles keyboard shortcuts (Ctrl+Enter to save, ESC to cancel).
 * Warns on outside click if there are unsaved changes.
 */
export const NotesPopover: React.FC<NotesPopoverProps> = ({
  isOpen,
  onClose,
  referenceElement,
  initialNotes,
  onSave,
  entityType,
  entityLabel,
}) => {
  const [draft, setDraft] = useState(initialNotes)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) onClose()
    },
    placement: 'right-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  })

  // Sync reference element
  useEffect(() => {
    refs.setReference(referenceElement)
  }, [referenceElement, refs])

  // Reset draft when opened
  useEffect(() => {
    if (isOpen) {
      setDraft(initialNotes)
      // Auto-focus textarea after render
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 0)
    }
  }, [isOpen, initialNotes])

  // Handle outside click with unsaved changes warning
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        referenceElement &&
        !referenceElement.contains(target)
      ) {
        const hasChanges = draft !== initialNotes
        if (hasChanges) {
          const confirmed = window.confirm(
            'You have unsaved changes. Discard them?'
          )
          if (confirmed) {
            onClose()
          }
        } else {
          onClose()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, draft, initialNotes, onClose, referenceElement])

  const handleSave = () => {
    onSave(draft.trim())
    onClose()
  }

  const handleCancel = () => {
    const hasChanges = draft !== initialNotes
    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Discard them?'
      )
      if (!confirmed) return
    }
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl/Cmd+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
    // ESC to cancel
    if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const charCount = draft.length
  const isOverLimit = charCount > MAX_CHARS

  if (!isOpen) return null

  return (
    <FloatingPortal>
      <div
        ref={(node) => {
          refs.setFloating(node)
          if (node) {
            popoverRef.current = node
          }
        }}
        style={floatingStyles}
        className="z-50 bg-white rounded-lg shadow-lg border-2 border-yellow-500 p-4 w-80"
        data-testid="notes-popover"
      >
        <div className="mb-2">
          <h3 className="text-sm font-semibold text-gray-700">
            {entityType === 'word' ? 'Word Note' : 'Rhyme Note'}:{' '}
            <span className="text-gray-900">{entityLabel}</span>
          </h3>
        </div>

        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full h-32 p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500"
          placeholder="Add your notes here..."
          maxLength={MAX_CHARS}
          data-testid="notes-popover-textarea"
        />

        <div className="mt-1 mb-3">
          <span
            className={`text-xs ${
              isOverLimit ? 'text-red-600 font-semibold' : 'text-gray-500'
            }`}
            data-testid="notes-popover-char-count"
          >
            {charCount}/{MAX_CHARS}
          </span>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            data-testid="notes-popover-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isOverLimit}
            className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="notes-popover-save"
          >
            Save
          </button>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          <kbd className="px-1 py-0.5 bg-gray-100 rounded">Ctrl+Enter</kbd> to
          save, <kbd className="px-1 py-0.5 bg-gray-100 rounded">ESC</kbd> to
          cancel
        </div>
      </div>
    </FloatingPortal>
  )
}
